import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import ClientDetailsClient from "@/components/ClientDetailsClient";
import { fetchClient } from "@/lib/api";
import { useEffect, useState } from "react";
import type {
  SerializedBusiness,
  SerializedBusinessUser,
  SerializedAgent,
  SerializedIntegration,
} from "@/types";

export const Route = createFileRoute("/dashboard/clients/$id")({
  component: ClientPage,
});

function ClientPage() {
  const { id } = Route.useParams();
  const location = useLocation();
  const [client, setClient] = useState<SerializedBusiness | null>(null);
  const [users, setUsers] = useState<SerializedBusinessUser[]>([]);
  const [agents, setAgents] = useState<SerializedAgent[]>([]);
  const [integrations, setIntegrations] = useState<SerializedIntegration[]>([]);
  const [loading, setLoading] = useState(true);

  const isClientPage = location.pathname === `/dashboard/clients/${id}`;

  useEffect(() => {
    // Also re-fetch on every navigation within this business's page tree (not
    // just when `id` changes) - $id.tsx is the shared parent layout for both
    // the detail view and nested routes like settings/, so navigating from
    // settings back to the detail page after a save doesn't remount this
    // component and would otherwise keep showing stale pre-edit data.
    fetchClient(id)
      .then((response) => {
        // API already serializes dates to strings
        setClient(response.client);
        setUsers(response.users);
        setAgents(response.agents);
        setIntegrations(response.integrations || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching client:", error);
        setLoading(false);
      });
  }, [id, location.pathname]);

  if (loading || !client) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isClientPage && (
        <div className="p-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <Link to="/dashboard/clients">
                <Button variant="ghost" size="sm" className="mb-4">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Clients
                </Button>
              </Link>
            </div>
            <ClientDetailsClient
              client={client}
              users={users}
              agents={agents}
              integrations={integrations}
            />
          </div>
        </div>
      )}
      <Outlet />
    </>
  );
}
